export const REVIEW_REQUIRED_MANUAL_VALUE = '__REVIEW_REQUIRED_MANUAL_VALUE__' as const;

export type ReviewRequiredManualValue = typeof REVIEW_REQUIRED_MANUAL_VALUE;
export type NumericConfigValue = number | ReviewRequiredManualValue;

export type InitialTuningConfig = {
  scan_start_freq_hz: NumericConfigValue;
  scan_end_freq_hz: NumericConfigValue;
  coarse_step_hz: NumericConfigValue;
  fine_step_hz: NumericConfigValue;
  fine_scan_bandwidth_hz: NumericConfigValue;
  dwell_time_ms: NumericConfigValue;
  exit_criteria: string;
};

export type AutotuneParamsConfig = {
  initial_step_size_hz: NumericConfigValue;
  max_bandwidth_hz: NumericConfigValue;
  min_peak_power_mw: NumericConfigValue;
  noise_threshold_mw: NumericConfigValue;
  sharp_drop_threshold: NumericConfigValue;
  max_iterations: NumericConfigValue;
  settle_time_ms: NumericConfigValue;
  convergence_power_threshold_mw: NumericConfigValue;
  convergence_freq_threshold_hz: NumericConfigValue;
  autotune_seek_maximum: boolean;
};

export type TuningTargetConfig = {
  target_id: string;
  description: string;
  priority: number;
  center_frequency_hz: NumericConfigValue;
  search_bandwidth_hz: NumericConfigValue;
  initial_tuning: InitialTuningConfig;
  autotune_params: AutotuneParamsConfig;
};

export type HardwareRevisionConfig = {
  revision_id: string;
  description: string;
  min_frequency_hz: NumericConfigValue;
  max_frequency_hz: NumericConfigValue;
  short_circuit_check_freq_hz: NumericConfigValue;
  tuning_targets: TuningTargetConfig[];
};

export type HardwareConfig = {
  hardware_revisions: HardwareRevisionConfig[];
};

export function normalizeConfigIdentifier(value: string): string {
  return value.trim().toLowerCase();
}

export function isReviewRequiredManualValue(value: unknown): value is ReviewRequiredManualValue {
  return value === REVIEW_REQUIRED_MANUAL_VALUE;
}

export function isNumericConfigValue(value: unknown): value is NumericConfigValue {
  return (
    (typeof value === 'number' && Number.isFinite(value)) ||
    isReviewRequiredManualValue(value)
  );
}

export function isOperationalNumericConfigValue(value: NumericConfigValue): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function isHardwareConfig(data: unknown): data is HardwareConfig {
  if (!isRecord(data)) {
    return false;
  }

  if (!Array.isArray(data.hardware_revisions)) {
    return false;
  }

  return data.hardware_revisions.every((item) => isHardwareRevisionConfig(item));
}

export function isHardwareRevisionConfig(data: unknown): data is HardwareRevisionConfig {
  if (!isRecord(data)) {
    return false;
  }

  if (typeof data.revision_id !== 'string') {
    return false;
  }

  if (typeof data.description !== 'string') {
    return false;
  }

  if (!isNumericConfigValue(data.min_frequency_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.max_frequency_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.short_circuit_check_freq_hz)) {
    return false;
  }

  if (!Array.isArray(data.tuning_targets)) {
    return false;
  }

  return data.tuning_targets.every((item) => isTuningTargetConfig(item));
}

export function isTuningTargetConfig(data: unknown): data is TuningTargetConfig {
  if (!isRecord(data)) {
    return false;
  }

  if (typeof data.target_id !== 'string') {
    return false;
  }

  if (typeof data.description !== 'string') {
    return false;
  }

  if (
    typeof data.priority !== 'number' ||
    !Number.isInteger(data.priority) ||
    data.priority < 0
  ) {
    return false;
  }

  if (!isNumericConfigValue(data.center_frequency_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.search_bandwidth_hz)) {
    return false;
  }

  if (!isInitialTuningConfig(data.initial_tuning)) {
    return false;
  }

  if (!isAutotuneParamsConfig(data.autotune_params)) {
    return false;
  }

  return true;
}

function isInitialTuningConfig(data: unknown): data is InitialTuningConfig {
  if (!isRecord(data)) {
    return false;
  }

  if (!isNumericConfigValue(data.scan_start_freq_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.scan_end_freq_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.coarse_step_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.fine_step_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.fine_scan_bandwidth_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.dwell_time_ms)) {
    return false;
  }

  return typeof data.exit_criteria === 'string';
}

function isAutotuneParamsConfig(data: unknown): data is AutotuneParamsConfig {
  if (!isRecord(data)) {
    return false;
  }

  if (!isNumericConfigValue(data.initial_step_size_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.max_bandwidth_hz)) {
    return false;
  }

  if (!isNumericConfigValue(data.min_peak_power_mw)) {
    return false;
  }

  if (!isNumericConfigValue(data.noise_threshold_mw)) {
    return false;
  }

  if (!isNumericConfigValue(data.sharp_drop_threshold)) {
    return false;
  }

  if (!isNumericConfigValue(data.max_iterations)) {
    return false;
  }

  if (!isNumericConfigValue(data.settle_time_ms)) {
    return false;
  }

  if (!isNumericConfigValue(data.convergence_power_threshold_mw)) {
    return false;
  }

  if (!isNumericConfigValue(data.convergence_freq_threshold_hz)) {
    return false;
  }

  return typeof data.autotune_seek_maximum === 'boolean';
}

function isRecord(data: unknown): data is Record<string, unknown> {
  return Boolean(data) && typeof data === 'object' && !Array.isArray(data);
}
